import {
  type InitAvo,
  normalizeAnalyticsEnvironment,
  registerAnalytics,
} from '@susu/analytics-client'

import type { Environment } from '../App'
import avo, { type AvoEnv } from '../avo/avo'

import { version } from '../../package.json'
import { debug } from '../utils/debug'

const APPLICATION_ID = avo.ApplicationId.CUSTOMER_HUB

let analyticsInitialized = false

type AvoSystemPropertiesInput = Parameters<typeof avo.setSystemProperties>[0]

let profileSystemProperties: Partial<AvoSystemPropertiesInput> = {}

/**
 * The profile being viewed, not the storefront page around it, is what every
 * event describes. Set on the generated client directly, because a composed
 * mount is not registered with @susu/analytics-client until a host
 * initializes, and kept so `initAvo` re-applies it over the page-level values.
 */
export function setProfileSystemProperties(
  properties: Partial<AvoSystemPropertiesInput>,
) {
  profileSystemProperties = properties
  avo.setSystemProperties(properties as AvoSystemPropertiesInput)
}

export interface CustomerHubAnalyticsConfig {
  /**
   * Whether this mount owns the page. Customer Hub is the page owner in the
   * RP shells and composed inside the storefront; only the owner loads the
   * transport. Defaults to `false`, the safe direction — a composed mount that
   * wrongly loaded a transport would double-deliver every event.
   */
  ownsPage?: boolean
}

/** Suppresses default Avo `[avo] ...` console output from the generated client. */
const silentAvoLogger = {
  logDebug: () => true,
  logWarn: () => true,
  logError: () => true,
} as const

const initAvo: InitAvo = (
  options,
  systemProperties,
  destinationOptions,
  segmentDestination,
) =>
  avo.initAvo(
    {
      env: (options.env === 'prod' ? 'prod' : 'dev') as AvoEnv,
      webDebugger: false,
      avoLogger: silentAvoLogger,
    },
    {
      ...systemProperties,
      ...profileSystemProperties,
    } as unknown as Parameters<typeof avo.initAvo>[1],
    destinationOptions,
    {
      ...segmentDestination,
      // The one guard for every event: analytics failures (e.g. Segment
      // blocked by an ad blocker) must never crash the app. Logged because a
      // missing Avo destination looks identical to "nobody clicked anything".
      logEvent: (eventName, eventProperties) => {
        try {
          segmentDestination.logEvent(eventName, eventProperties)
        } catch (error) {
          debug.warn('[customer-hub] analytics event dropped:', error)
        }
      },
    } as Parameters<typeof avo.initAvo>[3],
  )

export function initializeAnalytics(
  env: Environment,
  analyticsConfig: CustomerHubAnalyticsConfig = {},
) {
  if (analyticsInitialized) return
  analyticsInitialized = true

  registerAnalytics({
    surface: {
      // Customer Hub runs on a colleague device (the RP shells, PredictSpring,
      // ACL), so it writes to the In Store Webviews source and there is no
      // consumer consent banner to gate on. It previously wrote to a third
      // source set that belonged to neither the website nor the in-store group.
      placement: 'inStore',
      environment: normalizeAnalyticsEnvironment(env),
      ownsPage: analyticsConfig.ownsPage ?? false,
    },
    applicationId: APPLICATION_ID,
    version,
    initAvo,
    // Without this the client has nowhere to push GA identity once GA4's
    // cookies settle, so it stays frozen at its init-time value.
    setAvoSystemProperties: avo.setSystemProperties as unknown as (
      properties: Record<string, unknown>,
    ) => void,
  })
}
